import Check from "@mui/icons-material/Check";
import { Box, Stack, Typography } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useParams } from "react-router";
import { formatNumber } from "util/formatNumber";
import { WASTE_LINES_UNIFIED_QUERY } from "../../ManifestLineItems/Preview/api";

export const LineItemsDataGrid = ({
  processedManifestId,
  onRowClick,
}: {
  processedManifestId: string | undefined;
  onRowClick: (id: string) => void;
}) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { processedManifest } = useProcessedManifest(processedManifestId);

  return (
    <Stack spacing={3}>
      <Typography variant="subtitle1">Manifest Line Items</Typography>

      {processedManifestId && (
        <OmnisearchDataGrid
          persistenceKey={"manifest-detail-line-items"}
          columns={[
            {
              field: "lineItem",
              headerName: "Line Item",
              flex: 1,
              renderCell: ({ row: { willNotReport }, value }) => {
                return (
                  <Box>
                    <TruncateTypography variant="body2">
                      {value}
                    </TruncateTypography>
                    {willNotReport && (
                      <Typography variant="caption" color="text.secondary">
                        Not Reportable
                      </Typography>
                    )}
                  </Box>
                );
              },
            },
            {
              field: "wasteLineNumber",
              headerName: "Line Item #",
              flex: 1,
            },
            { field: "profileNumber", headerName: "Profile #", flex: 1 },
            {
              field: "vendorAmount",
              headerName: "Amount (Vendor)",
              flex: 1,
              filterKeyType: "number",
              valueGetter: (_, row) => {
                const amount = row.vendorAmount;
                return amount ? `${formatNumber(amount)} lbs` : "";
              },
            },
            {
              field: "eManifestAmount",
              headerName: "Amount (e‑Manifest)",
              flex: 1,
              filterKeyType: "number",
              valueGetter: (_, row) => {
                const amount = row.eManifestAmount;
                return amount ? `${formatNumber(amount)} lbs` : "";
              },
            },
            {
              field: "managementMethodCode",
              headerName: "Management Method Code",
              flex: 1,
            },
            {
              field: "willNotReport",
              headerName: "Not Reportable",
              filterKeyType: "boolean",
              sortable: false,
              flex: 1,
              renderCell: ({ value }) => {
                if (value === true) {
                  return <Check />;
                }
                return null;
              },
            },
          ]}
          dataQuery={WASTE_LINES_UNIFIED_QUERY}
          getItems={(data) => data.wasteLinesUnified?.items ?? []}
          getCount={(data) => data.wasteLinesUnified?.count ?? 0}
          withPadding={false}
          rowHeight="auto"
          showManageColumns
          defaultSearch={`tenantId:${tenantId} processedManifestId:${processedManifestId}`}
          initialSortModel={[{ field: "wasteLineNumber", sort: "desc" }]}
          initialPageSize={5}
          pageSizeOptions={[5, 10]}
          hideFooter={false}
          onRowClick={({ row: { id } }) => onRowClick(id)}
          commandButtons={({ search }) => [
            <ExportDataButton
              key="export"
              disabled={!processedManifest}
              fileName={`waste-manifest-${processedManifestId}`}
              exportType="wasteManifestLines"
              tenantId={tenantId}
              filter={{ search }}
              iconOnly
            />,
          ]}
          columnVisibilityModel={{
            willNotReport: false,
          }}
        />
      )}
    </Stack>
  );
};
